import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { SIDEBAR_ICON_SIZE } from "@/constants/UiConstants"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconShieldCheck } from "@tabler/icons-react"
import { Files, Lock } from "lucide-react"
import type * as React from "react"
import type { UpsellProduct } from "./resolveSidebarNav"

type LockedModuleSidebarItemProps =
  | {
      variant?: "upsell"
      product: UpsellProduct
      onOpenUpsell: () => void
    }
  | {
      variant: "onboarding"
      id: string
      label: string
      icon: React.ComponentType<{ style?: React.CSSProperties }>
    }

export const LockedModuleSidebarItem = (
  props: LockedModuleSidebarItemProps,
) => {
  const { translate } = useTranslation()

  if (props.variant === "onboarding") {
    const { id, label, icon: Icon } = props
    // SidebarMenuButton's own `tooltip` prop only renders while the sidebar
    // is collapsed to icon mode (see ui/sidebar.tsx: TooltipContent is
    // `hidden={state !== "collapsed"}`). The onboarding hint needs to show in
    // the default expanded view too, so this wraps the row in an explicit
    // Tooltip instead of relying on that prop.
    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <SidebarMenuButton
              id={id}
              className="cursor-default text-muted-foreground hover:text-muted-foreground"
              aria-label={label}
            >
              <Icon
                style={{
                  height: SIDEBAR_ICON_SIZE,
                  width: SIDEBAR_ICON_SIZE,
                }}
                aria-hidden="true"
              />
              <span className="truncate">{label}</span>
              <Lock className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
            </SidebarMenuButton>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="center"
            className="border border-muted-foreground bg-secondary-foreground text-card shadow-md"
          >
            {translate("DATA_ROOM/LOCKED_HINT")}
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    )
  }

  const { product, onOpenUpsell } = props
  const id =
    product === "mc"
      ? "navItemMaterialCompliance"
      : "navItemQuestionnaireLocked"
  const label = translate(
    product === "mc"
      ? "NAV_MATERIAL_COMPLIANCE"
      : "SIDEBAR/QUESTIONNAIRE_MODULE",
  )

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        id={id}
        onClick={onOpenUpsell}
        className="text-muted-foreground hover:text-muted-foreground"
        aria-label={label}
        tooltip={{
          children: label,
          side: "right",
          align: "center",
          className:
            "bg-secondary-foreground text-card shadow-md border border-muted-foreground",
        }}
      >
        {product === "mc" ? (
          <IconShieldCheck
            style={{ height: SIDEBAR_ICON_SIZE, width: SIDEBAR_ICON_SIZE }}
            aria-hidden="true"
          />
        ) : (
          <Files
            style={{ height: SIDEBAR_ICON_SIZE, width: SIDEBAR_ICON_SIZE }}
            aria-hidden="true"
          />
        )}
        <span className="truncate">{label}</span>
        <Lock className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
